import React, { useState, useEffect } from "react";
import { Card } from "react-bootstrap";
import {
  faSpinner,
  faExclamationTriangle,
  faHistory,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import api from "../../../axiosConfig";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* ===============================
   HELPER
================================ */
const getDuration = (start, end) => {
  if (!start) return "N/A";
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  if (isNaN(startDate) || isNaN(endDate)) return "N/A";
  const minutes = Math.floor((endDate - startDate) / 60000);
  return `${Math.max(0, minutes)} menit`;
};

const secondsToMinutes = (seconds) => {
  if (!Number.isInteger(seconds)) return "-";
  return `${Math.floor(seconds / 60)} menit`;
};

const HistoryTab = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedSector, setSelectedSector] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const secondsToMMSS = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
      return "00:00:00";
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return [
      String(hours).padStart(2, "0"),
      String(minutes).padStart(2, "0"),
      String(secs).padStart(2, "0"),
    ].join(":");
  };

  /* ===============================
     FETCH DATA
  ================================ */
  useEffect(() => {
    const fetchHistory = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Token tidak ditemukan. Silakan login terlebih dahulu.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const res = await api.get("/maintenance/history", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const normalized = res.data.map((row) => ({
          id: row.id,

          issue: row.issue,
          solution: row.solution,

          keterangan: row.keterangan || "-",
          uraianMasalah: row.uraian_masalah || "-",
          tindakanPenyebab: row.tindakan_penyebab || "-",

          reportTime: row.start_time,
          startTime: row.start_time,
          endTime: row.end_time,

          waktuTunggu: row.downtime_minutes || "00:00:00",

          totalPause: row.total_pause_seconds || "00:00:00",

          effectiveDuration: row.effective_minutes || "00:00:00",

          machineName: row.machine_name || "-",
          machineCode: row.machine_code || "-",
          sectorName: row.sector_name || "-",
          source: row.source || "-",
        }));

        // ✅ SET DI SINI
        setHistory(normalized);
        setFilteredHistory(normalized);
        setError(null);
      } catch (err) {
        if (err.response?.status === 401) {
          setError("Unauthorized! Token invalid atau expired.");
        } else {
          setError("Terjadi kesalahan saat memuat data.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  useEffect(() => {
    let data = [...history];

    // FILTER SEKTOR
    if (selectedSector !== "ALL") {
      data = data.filter((item) => item.sectorName === selectedSector);
    }

    // FILTER TANGGAL
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      data = data.filter(
        (item) => item.reportTime && new Date(item.reportTime) >= start,
      );
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      data = data.filter(
        (item) => item.reportTime && new Date(item.reportTime) <= end,
      );
    }

    // 🔎 FILTER SEARCH
    if (search) {
      const keyword = search.toLowerCase();
      data = data.filter(
        (item) =>
          item.machineName?.toLowerCase().includes(keyword) ||
          item.machineCode?.toLowerCase().includes(keyword) ||
          item.sectorName?.toLowerCase().includes(keyword) ||
          item.solution?.toLowerCase().includes(keyword) ||
          item.issue?.toLowerCase().includes(keyword),
      );
    }

    setFilteredHistory(data);
  }, [history, selectedSector, startDate, endDate, search]);

  const sectorOptions = [
    "ALL",
    ...Array.from(new Set(history.map((h) => h.sectorName))).filter(Boolean),
  ];

  const handleDownloadExcel = () => {
    if (!history || history.length === 0) return;

    // Export mengikuti SEMUA kolom yang tampil di tabel (urutan & nilai sama).
    const data = filteredHistory.map((a, i) => {
      const sourceUpper = (a.source || "").toUpperCase();

      return {
        No: i + 1,
        Mesin: a.machineName,
        Kode_Mesin: a.machineCode,
        Sektor: a.sectorName,
        Tanggal: a.reportTime
          ? new Date(a.reportTime).toLocaleDateString("id-ID")
          : "-",
        Mulai: a.reportTime
          ? new Date(a.reportTime).toLocaleTimeString("id-ID", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-",
        Waktu_Tunggu: a.waktuTunggu || "-",
        Waktu_Pause: a.totalPause || "-",
        Selesai_Perbaikan: a.endTime
          ? new Date(a.endTime).toLocaleString("id-ID", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-",
        Durasi_Perbaikan: a.effectiveDuration || "-",
        Problem: a.uraianMasalah || "-",
        Penyelesaian: a.tindakanPenyebab || "-",
        Keterangan:
          sourceUpper === "PERAWATAN"
            ? a.uraianMasalah || "-"
            : a.keterangan || a.uraianMasalah || "-",
        Sumber: sourceUpper === "PERAWATAN" ? "Perawatan" : a.source || "-",
        Status: a.endTime ? "Selesai" : a.isPaused ? "Pause" : "Proses",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Riwayat Maintenance");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `riwayat_maintenance_${Date.now()}.xlsx`);
  };

  /* ===============================
     STATE UI
  ================================ */
  if (loading) return <Loading />;
  if (error) return <Error message={error} />;
  if (history.length === 0) return <Empty />;

  /* ===============================
     RENDER
  ================================ */

  const tdStyle = {
    border: "1px solid #dbe2ea",
    padding: "12px 10px",
    textAlign: "center",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  };
  return (
    <>
      <Card
        style={{
          height: "calc(100vh - 70px)",
          marginTop: "-10px",
          borderRadius: "12px",
          border: "none",
          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HEADER */}
        <Card.Header
          style={{
            background: "#fff",
            borderBottom: "1px solid #e9ecef",
            padding: "18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontWeight: "700",
              color: "#1e293b",
            }}
          >
            Riwayat Maintenance
          </h4>

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              marginLeft: "auto",
            }}
          >
            {/* SEKTOR */}
            <div>
              <label className="small fw-bold text-secondary">Sektor</label>
              <select
                className="form-select form-select-sm"
                value={selectedSector}
                onChange={(e) => setSelectedSector(e.target.value)}
              >
                {sectorOptions.map((s, i) => (
                  <option key={i} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* DARI */}
            <div>
              <label className="small fw-bold text-secondary">
                Dari Tanggal
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            {/* SAMPAI */}
            <div>
              <label className="small fw-bold text-secondary">
                Sampai Tanggal
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            {/* SEARCH */}
            <div>
              <label className="small d-block opacity-0">Cari</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="🔍 Cari..."
                style={{ width: "220px" }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* EXPORT */}
            <div>
              <label className="small d-block opacity-0">Export</label>
              <button
                className="btn btn-success btn-sm"
                onClick={handleDownloadExcel}
                style={{
                  fontWeight: "600",
                  paddingInline: "16px",
                }}
              >
                Export Excel
              </button>
            </div>
          </div>
        </Card.Header>

        {/* BODY */}
        <Card.Body
          style={{
            padding: 0,
            flex: 1,
            overflow: "hidden",
            background: "#fff",
          }}
        >
          <div
            style={{
              height: "100%",
              overflowY: "auto",
              overflowX: "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                minWidth: "1650px",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                }}
              >
                <tr>
                  {[
                    "No",
                    "Mesin",
                    "Kode",
                    "Sektor",
                    "Tanggal",
                    "Mulai",
                    "Waktu Tunggu",
                    "Waktu Pause",
                    "Selesai perbaikan",
                    "Durasi Perbaikan",
                    "Problem",
                    "Penyelesaian",
                    "Keterangan",
                    "Sumber",
                    "Status",
                  ].map((head, i) => (
                    <th
                      key={i}
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        background: "#1e3a8a",
                        color: "#fff",
                        padding: "14px 10px",
                        border: "1px solid #dbe2ea",
                        boxShadow: "inset 0 -1px 0 #dbe2ea",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        fontWeight: "600",
                        fontSize: "13px",
                      }}
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredHistory.map((a, i) => (
                  <tr
                    key={a.id || i}
                    style={{
                      background: i % 2 === 0 ? "#ffffff" : "#f8fafc",
                      transition: "0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#eef4ff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        i % 2 === 0 ? "#ffffff" : "#f8fafc";
                    }}
                  >
                    <td style={tdStyle}>{i + 1}</td>
                    <td
                      className="cell-left"
                      style={{ ...tdStyle, textAlign: "left" }}
                    >
                      {a.machineName}
                    </td>
                    <td style={tdStyle}>{a.machineCode}</td>
                    <td style={tdStyle}>{a.sectorName}</td>

                    <td style={tdStyle}>
                      {a.reportTime
                        ? new Date(a.reportTime).toLocaleDateString("id-ID")
                        : "-"}
                    </td>

                    <td style={tdStyle}>
                      {a.reportTime
                        ? new Date(a.reportTime).toLocaleTimeString("id-ID", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "-"}
                    </td>

                    <td style={tdStyle}>{a.waktuTunggu || "-"}</td>

                    <td style={tdStyle}>{a.totalPause || "-"}</td>

                    <td style={tdStyle}>
                      {a.endTime
                        ? new Date(a.endTime).toLocaleString("id-ID", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "-"}
                    </td>

                    <td style={tdStyle}>{a.effectiveDuration || "-"}</td>

                    {/* Problem = masalah yang dilaporkan user */}
                    <td
                      className="cell-left"
                      style={{
                        ...tdStyle,
                        textAlign: "left",
                        whiteSpace: "normal",
                        maxWidth: "220px",
                        wordBreak: "break-word",
                      }}
                    >
                      {a.uraianMasalah || "-"}
                    </td>

                    {/* Penyelesaian = tindakan/solusi yang diinput teknisi */}
                    <td
                      className="cell-left"
                      style={{
                        ...tdStyle,
                        textAlign: "left",
                        whiteSpace: "normal",
                        maxWidth: "220px",
                        wordBreak: "break-word",
                      }}
                    >
                      {a.tindakanPenyebab || "-"}
                    </td>

                    <td
                      className="cell-left"
                      style={{
                        ...tdStyle,
                        textAlign: "left",
                        whiteSpace: "normal",
                        maxWidth: "220px",
                        wordBreak: "break-word",
                      }}
                    >
                      {(a.source || "").toUpperCase() === "PERAWATAN"
                        ? a.uraianMasalah || "-"
                        : a.keterangan || a.uraianMasalah || "-"}
                    </td>

                    <td style={tdStyle}>
                      <span
                        className={`badge ${
                          (a.source || "").toUpperCase() === "PERAWATAN"
                            ? "bg-primary"
                            : "bg-warning text-dark"
                        }`}
                      >
                        {(a.source || "").toUpperCase() === "PERAWATAN"
                          ? "Perawatan"
                          : a.source || "-"}
                      </span>
                    </td>

                    <td style={tdStyle}>
                      <span
                        className={`badge ${
                          a.endTime
                            ? "bg-success"
                            : a.isPaused
                              ? "bg-warning text-dark"
                              : "bg-secondary"
                        }`}
                      >
                        {a.endTime
                          ? "Selesai"
                          : a.isPaused
                            ? "Pause"
                            : "Proses"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

/* ===============================
   COMPONENT STATES
================================ */
const Loading = () => (
  <div className="text-center p-5">
    <FontAwesomeIcon
      icon={faSpinner}
      spin
      size="3x"
      className="text-primary mb-3"
    />
    <h5>Memuat data riwayat...</h5>
  </div>
);

const Error = ({ message }) => (
  <div className="text-center p-5 text-danger">
    <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="mb-3" />
    <h5>{message}</h5>
  </div>
);

const Empty = () => (
  <div className="text-center p-5">
    <FontAwesomeIcon icon={faHistory} size="3x" className="text-muted mb-3" />
    <h5>Tidak ada riwayat maintenance ditemukan</h5>
  </div>
);

export default HistoryTab;
